import React, { useEffect, useState, useRef, useCallback } from "react";
import _ from "underscore";
import "react-day-picker/style.css";
import { DayPicker } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import clsx from "clsx";
import {
  formatDate
} from "../../../utils/formatter"
import useOutsideAlerter from "../../../utils/clickOutside";

import styles from "./insights.module.css";
import { insightsInstitution } from "../../../services/insights";
import { useInstitution } from "../../../context/InstitutionContext";
import { useMantenedora, VIEW_MODES } from "../../../context/MantenedoraContext";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import ZeroData from "../../../components/zeroData/zeroData";
import InsightsIcon from "../../../assets/icons/insights-icon.svg";
import CalendarIcon from "../../../assets/icons/calendar.svg";
import BarsFilterIcon from "../../../assets/icons/bars-filter.svg";
import MagnifyingGlassIcon from "../../../assets/icons/magnifying-glass-blue.svg";

import Button from "../../../components/button/button";
import DisciplineIndexTable from "../../../components/insights/disciplineIndexTable/disciplineIndexTable";
import TopStudentsRatesTable from "../../../components/insights/topStudentsRatesTable/topStudentsRatesTable";
import OverdueIssuedByCourseTable from "../../../components/insights/overdueIssuedByCourseTable/overdueIssuedByCourseTable";
import InadimplenciaCard from "../../../components/insights/inadimplenciaCard/inadimplenciaCard";
import RevenueCard from "../../../components/insights/revenueCard/revenueCard";
import CourseRevenueCard from "../../../components/insights/courseRevenueCard/courseRevenueCard";
import DayRevenueCard from "../../../components/insights/dayRevenueCard/dayRevenueCard";
import WeekdayRevenueCard from "../../../components/insights/weekdayRevenueCard/weekdayRevenueCard";
import StudentCard from "../../../components/insights/studentCard/studentCard";
import ChartMixedUpCircleDollarIcon from "../../../assets/icons/chart-mixed-up-circle-dollar.svg";
import ChartMixedDownCircleDollarIcon from "../../../assets/icons/chart-mixed-down-circle-dollar.svg";
import ChartUserCheckIcon from "../../../assets/icons/chart-user-check.svg";
import TrophyIcon from "../../../assets/icons/trophy.svg";
import CalendarPlusIcon from "../../../assets/icons/calendar-plus.svg";
import CalendarMinusIcon from "../../../assets/icons/calendar-minus.svg";
import CalendarDayIcon from "../../../assets/icons/calendar-day.svg";
import CalendarWeekIcon from "../../../assets/icons/calendar-week.svg";
import MoneyDelayIcon from "../../../assets/icons/money-delay.svg";
import ChartCourseUpIcon from "../../../assets/icons/chart-course-up.svg";
import ChartCourseDownIcon from "../../../assets/icons/chart-course-down.svg";
import StudentPerformanceIcon from "../../../assets/icons/student-performance.svg";
import loadingGif from "../../../assets/loading.gif";
import { getDateRange, saveDateRange } from "../../../utils/localStorage";
import AiInsightsBlock from "../../../components/aiInsights/aiInsightsBlock";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { FEATURE_FLAG_KEYS } from "../../../services/featureFlags";

const Insights = () => {
  const { institution } = useInstitution();
  const { 
    viewMode, 
    selectedInstitutionId, 
    institutions, 
    isConsolidatedView
  } = useMantenedora();
  const calendarRef = useRef(null);
  
  const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determinar qual institution ID usar para as queries
  const getEffectiveInstitutionId = useCallback(() => {
    if (isConsolidatedView) return null;
    if (selectedInstitutionId) return selectedInstitutionId;
    return institution?.id;
  }, [isConsolidatedView, selectedInstitutionId, institution?.id]);

  const [suggestionRange, setSuggestionRange] = useState("");
  const [dateRangeString, setDateRangeString] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    return getDateRange() || { from: new Date(new Date().getFullYear(), 0, 1), to: new Date() };
  });

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("normal");

  const aiInsightsFlag = useFeatureFlag(FEATURE_FLAG_KEYS.AI_INSIGHTS_TAB);
  const isAiInsightsBlocked = aiInsightsFlag.blocked === true;

  useEffect(() => {
    if (isAiInsightsBlocked && activeTab === "ai") {
      setActiveTab("normal");
    }
  }, [isAiInsightsBlocked, activeTab]);

  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false));

  // Persistir data no localStorage para compartilhar entre telas
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      saveDateRange(dateRange);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchInsightsData({
      startDate: formatDate(dateRange?.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange?.to).replaceAll("/", "-")
    });
  }, []);

  // Refazer fetch quando a instituição selecionada ou modo de visualização muda
  useEffect(() => {
    fetchInsightsData({
      startDate: formatDate(dateRange?.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange?.to).replaceAll("/", "-")
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedInstitutionId]);

  useEffect(() => {
    setDateRangeString(
      `${formatDate(dateRange?.from)} - ${formatDate(dateRange?.to)}`
    );
  }, [dateRange]);

  // Função para agregar dados de insights de múltiplas instituições
  const aggregateInsightsData = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return null;
    if (dataArray.length === 1) return dataArray[0];

    const aggregated = JSON.parse(JSON.stringify(dataArray[0]));
    
    // Soma campos numéricos
    const sumFields = ['totalRevenue', 'totalStudents', 'totalDelinquency'];
    
    for (let i = 1; i < dataArray.length; i++) {
      const data = dataArray[i];
      sumFields.forEach(field => {
        aggregated[field] = (aggregated[field] || 0) + (data[field] || 0);
      });
    }

    // Média para taxas
    const avgFields = ['delinquencyRate', 'averageTicket'];
    avgFields.forEach(field => {
      let total = 0;
      let count = 0;
      dataArray.forEach(data => {
        if (data[field] !== undefined) {
          total += data[field];
          count++;
        }
      });
      if (count > 0) {
        aggregated[field] = total / count;
      }
    });

    // Concatena arrays
    const arrayFields = ['revenueByMonth', 'revenueByCourse', 'revenueByDay', 
                         'revenueByWeekday', 'delinquencyAnalysis', 'studentStats'];
    arrayFields.forEach(field => {
      if (Array.isArray(aggregated[field])) {
        dataArray.slice(1).forEach(data => {
          if (Array.isArray(data?.[field])) {
            aggregated[field] = [...aggregated[field], ...data[field]];
          }
        });
      }
    });

    return aggregated;
  };

  const fetchInsightsData = useCallback(
    _.debounce(async ({
      startDate,
      endDate
    }) => {
      try {
        setLoading(true);
        
        const effectiveId = getEffectiveInstitutionId();
        
        if (isConsolidatedView && institutions.length > 0) {
          // Modo consolidado
          const promises = institutions.map(inst => 
            insightsInstitution({
              institutionId: inst.id,
              startDate,
              endDate
            }).catch(err => {
              console.warn(`Erro ao buscar dados da instituição ${inst.id}:`, err);
              return null;
            })
          );
          
          const results = await Promise.all(promises);
          const validResults = results
            .filter(r => r !== null && r.insights)
            .map(r => r.insights);
          
          const aggregatedData = aggregateInsightsData(validResults);
          setInsightsData(aggregatedData);
        } else {
          // Modo individual
          const institutionId = effectiveId || institution?.id;
          if (!institutionId) {
            console.warn("Nenhuma instituição selecionada");
            setLoading(false);
            return;
          }
          
          const response = await insightsInstitution({
            institutionId,
            startDate,
            endDate
          });
          setInsightsData(response.insights);
        }
        
        setIsCalendarOpen(false);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }, 1000),
    [getEffectiveInstitutionId, isConsolidatedView, institutions, institution?.id]
  );

  const selectManualRange = () => {
    setSuggestionRange("");
    fetchInsightsData({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-")
    });
  };

  const selectSuggestionRange = (suggestionRange) => {
    setSuggestionRange(suggestionRange);
    const currentYear = new Date().getFullYear();

    let selectedRange;
    switch (suggestionRange) {
      case "today":
        selectedRange = {
          from: new Date(),
          to: new Date()
        };
        break;

      case "last_7":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 7)),
          to: new Date()
        };
        break;

      case "last_30":
        const last_30_now = new Date();
        selectedRange = {
          from: new Date(last_30_now.getFullYear(), last_30_now.getMonth(), 1),
          to: new Date(last_30_now.getFullYear(), last_30_now.getMonth() + 1, 0)
        };
        break;

      case "first_semester":
        selectedRange = {
          from: new Date(`${currentYear}-01-01T00:00:00`),
          to: new Date(`${currentYear}-06-30T23:59:59`)
        };
        break;

      case "second_semester":
        selectedRange = {
          from: new Date(`${currentYear}-07-01T00:00:00`),
          to: new Date(`${currentYear}-12-31T23:59:59`)
        };
        break;

      case "last_year":
        const now = new Date();
        selectedRange = {
          from: new Date(now.getFullYear() - 1, now.getMonth(), 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0)
        };
        break;

      case "last_5_years":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 5)),
          to: new Date()
        };
        break;

      case "current_year":
        selectedRange = {
          from: new Date(currentYear, 0, 1),
          to: new Date()
        };
        break;

      default:
        selectedRange = {
          from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
          to: new Date(new Date().setMonth(new Date().getMonth() - 0))
        };
        break;
    }

    setDateRange(selectedRange);

    fetchInsightsData({
      startDate: formatDate(selectedRange.from).replaceAll("/", "-"),
      endDate: formatDate(selectedRange.to).replaceAll("/", "-")
    });
  };

  const handleClearSelection = () => {
    setSuggestionRange("");
    setDateRange({
      from: new Date(),
      to: new Date()
    });
  };

  return (
    <div className={styles.insightsContainer}>
      <HeaderTitle title="Insights" icon={InsightsIcon}>
        <div className={styles.headerTitleActions}>
          {activeTab !== "ai" && (
          <div className={styles.headerTitleActionsCalendar}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div ref={calendarRef} className={styles.calenderContent}>
                <div className={styles.suggestions}>
                  <ul>
                    <li
                      onClick={() => !loading && selectSuggestionRange("today")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "today"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Hoje
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_7")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_7"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 7 dias
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_30")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_30"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Último mês
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("first_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "first_semester"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Primeiro Semestre
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("second_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "second_semester"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Segundo Semestre
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_year"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 12 meses
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("current_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "current_year"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Atual
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_5_years")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_5_years"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 5 Anos
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("manual")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "manual"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Personalizado
                    </li>
                  </ul>
                  <div>
                    <div
                      onClick={() => !loading && handleClearSelection()}
                      className={clsx(styles.suggestionsItem, styles.active)}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Limpar seleção
                    </div>
                    <div
                      onClick={() => !loading && selectManualRange()}
                      className={clsx(
                        styles.suggestionsItem,
                        styles.active,
                        styles["btn-search-data"]
                      )}
                      style={{ 
                        opacity: loading ? 0.8 : 1, 
                        cursor: loading ? 'not-allowed' : 'pointer' 
                      }}
                    >
                      {loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <img
                            src={loadingGif}
                            alt="Carregando..."
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span>Carregando...</span>
                        </div>
                      ) : (
                        <>
                          <img src={MagnifyingGlassIcon} alt="magnifying-glass" />
                          Buscar dados
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.dateRangeGroup}>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Início</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.from}
                      onSelect={(from) => setDateRange((prev) => ({ ...prev, from }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Fim</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.to}
                      onSelect={(to) => setDateRange((prev) => ({ ...prev, to }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          <Button
            icon={BarsFilterIcon}
            text="Filtrar"
            size="normal"
            variation="outline"
            disabled={true}
          />
        </div>
      </HeaderTitle>

      <div className={styles.tabsBar} role="tablist" aria-label="Abas de insights">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "normal"}
          className={clsx(styles.tabButton, {
            [styles.tabButtonActive]: activeTab === "normal"
          })}
          onClick={() => setActiveTab("normal")}
        >
          Insights
        </button>
        {!isAiInsightsBlocked && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "ai"}
            className={clsx(styles.tabButton, {
              [styles.tabButtonActive]: activeTab === "ai"
            })}
            onClick={() => setActiveTab("ai")}
          >
            Insights <span className={styles.tabAiBadge}>IA</span>
          </button>
        )}
      </div>

      <div className={`${styles.financeContent} .insights-content`}>
        {!isAiInsightsBlocked && activeTab === "ai" && (
          <AiInsightsBlock
            institutionId={getEffectiveInstitutionId()}
            isConsolidatedView={isConsolidatedView}
          />
        )}

        {activeTab === "normal" && (loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px"
            }}
          >
            <img
              src={loadingGif}
              alt="Carregando..."
              style={{ width: "60px", height: "60px" }}
            />
          </div>
        ) : insightsData ? (
          <>
            {/* === SEÇÃO FATURAMENTO === */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Faturamento</h2>
              <div className={styles.grid_faturamento}>
                <div className={styles.grid_faturamento_row1}>
                  <CourseRevenueCard
                    courseData={insightsData.highestRevenueCourse}
                    icon={ChartCourseUpIcon}
                    title="Curso de Maior Faturamento"
                    explanation="Curso com maior valor faturado no período selecionado, mostrando qual curso gera mais receita para a instituição."
                    code="I-01"
                  />
                  
                  <CourseRevenueCard
                    courseData={insightsData.lowestRevenueCourse}
                    icon={ChartCourseDownIcon}
                    title="Curso de Menor Faturamento"
                    explanation="Curso com menor valor faturado no período selecionado, indicando cursos que podem precisar de atenção especial."
                    code="I-02"
                  />
                  
                  <CourseRevenueCard
                    courseData={insightsData.topDiscipline}
                    icon={TrophyIcon}
                    title="Curso com Maior Ticket Médio"
                    explanation="Curso com maior ticket médio no período selecionado."
                    code="I-03"
                  />
                </div>
                
                <div className={styles.grid_faturamento_row2}>
                  <RevenueCard
                    revenueData={insightsData.highestRevenueMonth}
                    icon={CalendarPlusIcon}
                    title="Mês de Maior Faturamento"
                    explanation="Mês com maior valor faturado no período selecionado, indicando o período de melhor performance financeira."
                    code="I-04"
                  />
                  
                  <RevenueCard
                    revenueData={insightsData.lowestRevenueMonth}
                    icon={CalendarMinusIcon}
                    title="Mês de Menor Faturamento"
                    explanation="Mês com menor valor faturado no período selecionado."
                    code="I-05"
                  />
                </div>
                
                <div className={styles.grid_faturamento_row3}>
                  <DayRevenueCard
                    dayData={insightsData.highestRevenueDay}
                    icon={CalendarDayIcon}
                    title="Dia do Mês com Maior Faturamento"
                    explanation="Dia do mês com maior valor recebido no período selecionado, mostrando qual dia teve melhor performance de recebimentos."
                    code="I-06"
                  />
                  
                  <WeekdayRevenueCard
                    weekdayData={insightsData.highestRevenueWeekday}
                    icon={CalendarWeekIcon}
                    title="Dia da Semana com Maior Faturamento"
                    explanation="Dia da semana com maior valor recebido no período selecionado, identificando padrões semanais de recebimento."
                    code="I-07"
                  />
                </div>
              </div>
            </div>

            {/* === SEÇÃO INADIMPLÊNCIA === */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Inadimplência</h2>
              <div className={styles.grid_inadimplencia}>
                <div className={styles.grid_inadimplencia_row1}>
                  <InadimplenciaCard
                    inadimplenciaData={insightsData.cursoMaiorInadimplencia}
                    icon={ChartMixedUpCircleDollarIcon}
                    explanation="Curso com maior percentual de inadimplência no período selecionado."
                    code="I-08"
                  />
                  
                  <InadimplenciaCard
                    inadimplenciaData={insightsData.cursoMenorInadimplencia}
                    icon={ChartMixedDownCircleDollarIcon}
                    explanation="Curso com menor percentual de inadimplência no período selecionado."
                    code="I-09"
                  />
                    
                  <InadimplenciaCard
                    inadimplenciaData={insightsData.topOverdueIssuedCourse}
                    icon={MoneyDelayIcon}
                    title="Maior Valor em Atraso"
                    explanation="Curso com maior valor emitido em atraso no período selecionado."
                    code="I-10"
                  />
                </div>
                
                <div className={styles.grid_inadimplencia_row2}>
                  <InadimplenciaCard
                    inadimplenciaData={insightsData.mesMaiorInadimplencia}
                    icon={CalendarPlusIcon}
                    explanation="Mês com maior percentual de inadimplência no período selecionado."
                    code="I-11"
                  />
                  
                  <InadimplenciaCard
                    inadimplenciaData={insightsData.mesMenorInadimplencia}
                    icon={CalendarMinusIcon}
                    explanation="Mês com menor percentual de inadimplência no período selecionado."
                    code="I-12"
                  />
                </div>
              </div>
            </div>

            {/* === SEÇÃO ALUNOS === */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Alunos</h2>
              <div className={styles.grid_2_cols}>
                <StudentCard
                  studentData={insightsData.bestStudentAverage}
                  icon={ChartUserCheckIcon}
                  title="Melhor Aluno por Média"
                  explanation="Aluno com maior média geral no período selecionado, destacando a excelência acadêmica na instituição."
                  code="I-13"
                />
                
                {/* 
                  ⚠️ NOTA: "Top Aluno - Desempenho" pode não ter dados disponíveis ainda
                  
                  Possíveis campos que podem conter essa informação:
                  - topStudentPerformance (campo atual usado)
                  - bestStudentPerformance
                  - topStudent
                  - studentWithBestPerformance
                  
                  Endpoint atual: GET api/v1/InsightsInstitution
                  Verifique o console do navegador para ver todos os campos disponíveis.
                  
                  Se o campo não existir na API, será necessário:
                  1. Solicitar ao backend a criação deste campo
                  2. Ou usar um endpoint alternativo de alunos
                  3. Ou calcular o desempenho baseado em outras métricas
                  
                  O componente StudentCard já trata a ausência de dados, mostrando 
                  "Nenhum dado encontrado" quando o campo não existe.
                */}
                <StudentCard
                  studentData={insightsData.topStudentPerformance}
                  icon={StudentPerformanceIcon}
                  title="Aluno com Maior Média Geral"
                  explanation="Aluno com melhor desempenho geral no período selecionado."
                  code="I-14"
                />
              </div>
            </div>
          </>
        ) : (
          <ZeroData>
            <h4>
              Você ainda não enviou dados suficientes para realizar análise de insights.
            </h4>
          </ZeroData>
        ))}
      </div>
    </div>
  );
};

export default Insights;
